/* eslint-disable @typescript-eslint/no-explicit-any */
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { StatefulService } from '@app/service/stateful.service';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { DictionaryService, FeatureFlagService } from '@cudirect/shared-ui-library';
import { LoanType } from '@shared/enum';
import { Result } from '@shared/model/result';
import { LenderService } from '@shared/service/lender/lender.service';
import { User_api } from '@user/constant/user-const';
import { ConsumerLogin } from '@user/model/consumer-login';
import { LoginResponse } from '@user/model/login.response.model';
import { CrudApiBackend } from '@workflow/class/crud-api-backend';
import { Allowed_Route } from '@workflow/ecp-shared/constant/allowed-route-const';
import { Info, RequestHeaderConst } from '@workflow/ecp-shared/constant/information.const';
import { ApplicationState, LoanTypeEnum, OAOAppStatus } from '@workflow/ecp-shared/enum/enum';
import { DataLoadURL } from '@workflow/ecp-shared/model/dataload-url';
import { Document } from '@workflow/ecp-shared/model/document.model';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { StorageWrapperService } from '@workflow/ecp-shared/service/storage-wrapper/storage-wrapper.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { StorageService } from '@workflow/service/storage/storage.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { last, mergeMap, Observable, switchMap } from 'rxjs';
import { CUDLConnect, PrefillRequest } from '../../component/building-block/app-prefill/app-prefill.type';
import { CustomUserInfo } from '../../types/custom-userinfo.type';
import { ExternalServiceData } from '../../types/externalServiceData.type';
import { AlloyPrefillData, AlloyService, AlloyVerificationResult } from '../alloy/alloy.service';
import { ApiResult, BundleResponse } from './application-api.types';
import { ConsentCaptureRequest } from './consent-capture-request.interface';

const SHARE_CERT_TYPE = ['retiresharecertificates', 'sharecertificates'];
/**
 * HTTP request methods for application related calls
 */
@Injectable({
  providedIn: 'root'
})
export class ApplicationApiService {
  private logger = inject(SawDust);

  /**
   * Currently ongoing application submit process
   */
  public submitting = false;
  /** Currently ongoing application submit process for OAO application */
  public OAOAppSubmitting = false;
  /** Currently ongoing application submit process for OLA application */
  public OLAAppSubmitting = false;

  /**
   * Result of submission
   */
  public submitResult: boolean;

  /** Consumer portal API Host Name */
  private readonly hostName = 'consumerPortalApi';

  public storagePrefix = 'api';

  /**
   * Partial application flag
   */
  public partialApplication = false;

  /**
   * Generate App Id flag
   */
  public generateAppId = false;

  /**
   * API path collection
   */
  protected readonly apiPath = {
    appPrefill: 'prefill',
    appPrefillComplete: 'prefill/ordercomplete',
    captureConsent: 'consent/capture',
    branchUser: 'consumer/mortgage/branchUser',
    branches: 'consumer/mortgage/branches',
    bundle: 'consumer/dynamic/bundle',
    discountPercent: 'consumer/mortgage/loan/pricing/subprogram',
    bundleOLA: 'consumer/ola/dynamic/bundle',
    hardCreditReport: 'consumer/mortgage/{{loanNumber}}/integration/hardcreditreport',
    helocDisclosures: 'document/legalDocument',
    helocDisclosuresAccepted: 'consumer/mortgage/dynamic/acceptHelocDisclosure?isAcceptedHeloc=true',
    oaoSubmit: 'member/oao/integration/SubmitApplication',
    olaSubmit: 'consumer/ola/dynamic/submit',
    automateDecision: 'consumer/ola/dynamic/runAutomatedDecision',
    pricingSchedule: 'consumer/mortgage/loan/pricing/pricingSchedule',
    pricing: 'consumer/mortgage/pricing',
    pricingEdm: 'consumer/edm/pricing/search',
    submissionStatus: 'consumer/mortgage/dynamic/submissionStatus',
    notificationStatus: 'postapplication/oao/getpostappnotification',
    submit: 'consumer/mortgage/dynamic/submit',
    voa: 'consumer/mortgage/{{loanNumber}}/integration/VOAGenerateIFrameUrl',
    voaAssets: 'consumer/mortgage/{{loanNumber}}/personal/asset/all',
    voaComplete: 'consumer/mortgage/{{loanNumber}}/integration/VOAGenerateIFrameUrl/completed',
    voi: 'consumer/mortgage/{{loanNumber}}/integration/voi',
    getUserById: 'admin/users/{{userId}}',
    // Just set this staic for now to get the ratesheet data to work
    rateSheet: 'consumer/mortgage/loan/pricing/GetBestExecution',
    amortizationSchedule: 'consumer/mortgage/loan/pricing/amortizationSchedule',
    emailAddress: 'Email/ComparisonPricing',
    register: 'account/register',
    silentRegister: 'account/silent-register',
    alloyPrefillLink: 'consumer/fraudcheck/prefill',
    alloyPrefillResendLink: 'consumer/fraudcheck/prefill/renew/{{index}}',
    alloyPrefillLinkVerify: 'consumer/fraudcheck/prefill/verify',
    alloyPrefillPrimaryComplete: 'consumer/fraudcheck/prefill/primary/complete',
    alloyJourney: 'consumer/fraudcheck/verify',
    alloyJourneyId: 'consumer/fraudcheck/{{entityId}}',
    save: 'consumer/application/save',
    finalSubmit: 'consumer/application/submit',
    logexternalservicedata: 'billablethirdpartytransaction/logexternalservicedata',
    savejourney: 'consumer/fraudcheck/savejourney'
  };

  /**
   * url base for API calls
   */
  protected apiRoot: string;

  /**
   * API backend for mortgage
   */
  protected get crudApiService(): CrudApiBackend {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    return this.storageService.backends[this.storagePrefix] as CrudApiBackend;
  }

  private readonly statefulService = inject(StatefulService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  constructor(
    protected readonly apiService: ApiService,
    protected readonly httpClient: HttpClient,
    protected readonly dictionary: DictionaryService,
    protected readonly storageService: StorageService,
    protected readonly userService: UserService,
    protected readonly workflowService: WorkflowService,
    public readonly lenderService: LenderService,
    public readonly featureFlagService: FeatureFlagService,
    private readonly alloy: AlloyService,
    public readonly storageWrapperService: StorageWrapperService
  ) {}

  /**
   * Centralized logging method to handle both ApplicationInsights and SawDust logging
   * @param eventName The name of the event to log
   * @param eventData The data to include with the event
   */
  private logEvent(eventName: string, eventData: Record<string, unknown>): void {
    this.logger.logEvent(eventName, eventData);
  }

  /** Initiates a prefill request on behalf of borrower
   * @param prefillRequest prefill request data of type PrefillRequest
   * @param cb callback
   * @param error onError
   */
  public appPrefill(prefillRequest: PrefillRequest, cb: (success: ApiResult<string>) => void, error: (err?: any) => void): void {
    this.prepareUrl('appPrefill', (url) => {
      this.httpClient.post<ApiResult<string>>(url, prefillRequest, this.httpOptions()).subscribe((res) => {
        cb(res);
      }, error);
    });
  }

  /** Initiates a prefill request on behalf of borrower
   * @param prefillRequest prefill request data of type PrefillRequest
   * @param cb callback
   * @param error onError
   */
  public getPrefillResponse(body, cb: (data: any) => void, error: (err?: any) => void): void {
    this.prepareUrl('appPrefillComplete', (url) => {
      this.httpClient.post<CUDLConnect>(url, body, this.httpOptions()).subscribe((res) => {
        cb(res);
      }, error);
    });
  }

  /**
   * This will call the API to associate a consent acceptance with the bundle if available, and the user.
   * @param storagePrefix The prefix used for header fetching (api, ola)
   * @param body The request body - contains information to associate with the consent and the user
   * @param withBundle Is this in a bundle context, if true, use the
   * standard wrap method with query params, if not, just call the endpoint directly.
   */
  public captureConsent(storagePrefix: string, body: ConsentCaptureRequest, withBundle: boolean = true) {
    this.storagePrefix = storagePrefix;
    if (withBundle) {
      this.prepareUrlForPost('captureConsent', (url) => {
        this.httpClient.post(url, body, this.httpOptions()).subscribe();
      });
    } else {
      this.apiRoot = this.apiService.GetFullURL('consumerPortalApi', '');
      // when called in a heavy registration scope, there is no bundle
      const urlObj = new URL(this.apiRoot + this.apiPath.captureConsent);
      const url = decodeURI(urlObj.href);
      this.httpClient.post(url, body, this.httpOptions()).subscribe();
    }
  }

  public branchUserList(role, branchId, cb: (err?: Error, data?: any) => void): void {
    this.prepareUrlForPost('branchUser', (url) => {
      const body = {
        role,
        branchId
      };
      this.httpClient.post<ApiResult<any>>(url, body, this.httpOptions()).subscribe(
        (res) => {
          this.statefulService.wrap('mem:show-card-spinner').value = false;
          cb(null, res.data);
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        (err) => {
          this.statefulService.wrap('mem:show-card-spinner').value = false;
        }
      );
    });
  }

  public branchList(cb: (err?: Error, data?: any) => void): void {
    this.prepareUrl('branches', (url) => {
      this.httpClient.get<ApiResult<any>>(url, this.httpOptions()).subscribe((res) => {
        cb(null, res.data);
      });
    });
  }

  /**
   * Method for submitting an OAO application.
   *
   * This method prepares and sends an HTTP POST request to a certain URL.
   * If the request is successful, it calls the callback function with the result.
   * In case of an error, it calls the callback function with the result and error as arguments.
   *
   * @param cb - The callback function to be called after the submission attempt.
   * It receives a boolean indicating the result of the submission and optionally an error object.
   */
  public submitOAOApplication(cb?: (isSuccess: boolean, err?: any) => void): void {
    // Check if a submission is already in progress or a result is already obtained, and if so, exit
    if (!this.canOAOSubmit()) return;

    // Set flags for init
    this.initOAOSubmission();
    this.prepareUrl('oaoSubmit', (url) => {
      // Use HttpClient to send the POST request
      this.httpClient.post(url, null, this.httpOptions()).subscribe({
        // Handle the response
        next: (data: ApiResult<any>) => {
          // NOTE: HTTP errors should trigger the error below, we won't get much info from the response otherwise
          if (data.isSuccess) {
            this.handleOAOSuccess(cb);
          } else {
            // Special error handling for OAO
            const errorResponse = `Reason: ${data?.reason || 'Unknown'} - Message: ${data?.message || 'Unknown'}`;
            this.handleOAOError(errorResponse, cb);
            // Log the error to the console
            this.globalErrorHandler(data);
          }
        },
        // If the HTTP request fails
        error: (error: HttpErrorResponse) => {
          // Extract the error object
          // NOTE: This is a different implementation than OLA - refactor when we implement errCb here
          const errObj = error?.error;

          // Handle an unsuccessful response
          this.handleOAOError(errObj, cb);
        }
      });
    });
  }

  /**
   * Checks if the submission can start based on several conditions.
   */
  private canOAOSubmit(): boolean {
    return !(this.submitting || this.submitResult || this.OAOAppSubmitting);
  }

  /**
   * Sets the initial state for the submission.
   */
  private initOAOSubmission(): void {
    // Set flags indicating the start of the submission process
    this.submitting = true;
    this.OAOAppSubmitting = true;
  }

  /**
   * Handles a successful submission.
   *
   * @param cb - The callback function to be called after the submission attempt.
   */
  private handleOAOSuccess(cb?: (isSuccess: boolean) => void): void {
    this.submitting = false;
    this.OAOAppSubmitting = false;
    this.submitResult = true;

    // If a callback function is provided, call it with the result
    if (cb) cb(this.submitResult);
  }

  /**
   * Handles an unsuccessful submission or failure of the HTTP request.
   *
   * @param error - The error object or data received from the unsuccessful response or request failure.
   * @param cb - The callback function to be called after the submission attempt.
   */
  private handleOAOError(error: any, cb?: (isSuccess: boolean, err?: any) => void): void {
    // Reset the flags and the result
    this.submitting = this.submitResult = this.OAOAppSubmitting = false;

    // If a callback function is provided, call it with the result and the error object
    if (cb) cb(this.submitResult, error);
  }

  /**
   * Method for submitting an OLA application.
   *
   * This method prepares and sends an HTTP request to a certain URL.
   * If the request is successful, it calls the callback function with the result.
   * In case of an error, it calls the error callback function and passes the error as an argument.
   *
   * @param cb - The callback function to be called after a successful submission.
   * It receives a boolean indicating the result of the submission.
   * @param errcb - The error callback function to be called in case of an error.
   * It receives the error object.
   * @remarks
   * TODO: Either submitOAOApplication should take an errcb, or neither should - normalize these functions
   */
  public submitOLAApplication(cb?: (res: boolean) => void, errcb?: (err: any) => void): void {
    if (!this.canOLASubmit()) return;
    this.initOLASubmission();
    if (this.isNonPartialOLAFeatureFlagEnabled()) {
      this.processNonPartialSubmit();
    } else {
      this.prepareUrl('automateDecision', (url) => {
        this.httpClient.get(url, this.httpOptions()).subscribe({
          next: (data: ApiResult<any>) => this.handleSuccess(data, cb, errcb),
          error: (error: HttpErrorResponse) => {
            this.submitting = this.submitResult = this.OLAAppSubmitting = false;
            this.globalErrorHandler(error);
            if (errcb) errcb(error);
          }
        });
      });
    }
  }

  /**
   * Method for submitting an OLA app using new submit endpoint
   * @param cb - The callback function to be called after a successful submission.
   * @param errcb - The error callback function to be called in case of an error.
   */
  public processNonPartialSubmit(cb?: (res: boolean) => void, errcb?: (err: any) => void): void {
    // Prepare the URL for the HTTP request
    this.prepareUrl('finalSubmit', (url) => {
      this.partialApplication = false;
      // Use HttpClient to send the POST request
      this.httpClient.get(url, this.httpOptions()).subscribe(
        // Handle the response
        (res: ApiResult<any>) => {
          this.handleSuccess(res, cb, errcb);
        },
        // If the HTTP request fails
        (error) => {
          this.logInsightsEvent(LoanType.OLA.toLowerCase(), 'Error: OLA Non-Partial Submit');
          const errObj = error?.error;
          this.globalErrorHandler(errObj);
          errcb(error);
        }
      );
    });
  }

  /**
   * Handles the successful response from the OLA application submission.
   *
   * This method checks the success status of the response, calls the callback function if provided,
   * and handles the error in case the submission was not successful.
   *
   * @param data - The response data from the submission.
   * @param cb - Optional callback function to be called after a successful submission.
   * @param errcb - Optional error callback function to be called in case of an error.
   */
  private handleSuccess(data: ApiResult<any>, cb?: (res: boolean) => void, errcb?: (err: any) => void): void {
    this.storagePrefix = 'api';
    // For Non Partial OLA Apps with Submission Queue enabled, since we do not have the App Number available right away on Submission,
    // Setting up the tempLoanNumber to load overview data until we get it back from the API
    if (this.isNonPartialOLAFeatureFlagEnabled()) {
      if (data.data?.applicationId && Number(data.data?.applicationId) < 0 && data.data?.tempLoanNumber) {
        this.storageWrapperService.set(Info.appNumber, data.data?.tempLoanNumber);
      }
    }
    if (data.isSuccess) {
      this.submitting = false;
      this.OLAAppSubmitting = false;
      this.submitResult = true;
      if (cb) cb(this.submitResult);
    } else {
      this.submitting = this.submitResult = this.OLAAppSubmitting = false;
      this.globalErrorHandler(data);
      if (errcb) errcb(data);
    }
  }

  /**
   * Checks if the submission can start based on several conditions.
   */
  private canOLASubmit(): boolean {
    return !(this.submitting || this.submitResult || this.OLAAppSubmitting);
  }

  /**
   * Sets the initial state for the submission.
   */
  private initOLASubmission(): void {
    // Indicate that a submission is in progress and an OLA application is being submitted.
    this.submitting = true;
    this.OLAAppSubmitting = true;

    // Set the storage prefix to 'ola'.
    this.storagePrefix = 'ola';
  }

  /**
   * update bundle data to DDMS
   */
  public saveBundleToDDMS(cb?: () => void): void {
    this.prepareUrl('bundle', (url) => {
      this.httpClient.put(url, null, this.httpOptions()).subscribe({
        next: () => {
          if (cb) {
            cb();
          }
        },
        error: () => {
          if (cb) {
            cb();
          }
        }
      });
    });
  }

  /**
   * Set user data to storage
   */
  public setUserForOaoPostApp(): void {
    const rawUser = this.userService.user;
    const userInfo: UserInfo = typeof rawUser === 'string' ? JSON.parse(rawUser) : rawUser || ({} as UserInfo);
    userInfo.email = this.statefulService.wrap(`api:email`).valueString;
    userInfo.token_time = new Date();
    userInfo.firstTimeLogin = this.statefulService.wrap(`api:firstTimeLogin`).valueString === 'true';
    const firstName = this.statefulService.wrap(`api:oaoMemberInformation/primaryApplicant/firstName`).valueString;
    const lastName = this.statefulService.wrap(`api:oaoMemberInformation/primaryApplicant/lastName`).valueString;
    userInfo.firstName = firstName;
    userInfo.lastName = lastName;
    userInfo.userName = `${firstName} ${lastName}`;
    this.userService.user = userInfo;
    this.userService.appNumber = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString;
    this.userService.allowedRoutes = Allowed_Route.OAO_PostApp;
    this.userService.flowType = 'OAO';
    this.userService.loanStatus = ApplicationState.StatusInProgress;
    this.userService.statusMessage = `Hi ${firstName}, we're reviewing your application now.`;
    this.userService.accountsNamesLabel = 'Requested accounts';
    this.userService.accountNames = this.getSubmittedAccounts();
    this.userService.appStatus = OAOAppStatus.Submitted;
    this.userService.applicants = `${firstName} ${lastName}`;
    this.userService.loanProductName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipName').valueString;
    this.userService.loanGroupIcon = null;
    this.userService.loanProductDisplayName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipName').valueString;
    this.userService.requestedLoanAmount = null;
    this.userService.decision_amount = null;
    this.userService.decision_rate = null;
    this.userService.decision_term = null;
    this.userService.decision_estMonthlyPayment = null;
    this.userService.membershipStatus = OAOAppStatus.Submitted;
    this.userService.membershipTypeName = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipTypeName').valueString;
    this.userService.isYouthMembership = this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').value as boolean;
  }

  /**
   * Get Selected Accounts
   * @returns string
   */
  public getSubmittedAccounts(): string {
    const accountNames = [];
    const shareBaseId = 'api:oaoMemberInformation/accountsAndServices/primaryApplicant/shares';

    // Getting Selected Shares
    const sharesCount = this.statefulService.arraySize(shareBaseId);
    for (let i = 0; i < sharesCount; i++) {
      const isSelected = this.statefulService.wrap(`${shareBaseId}/${i}/isSelected`).value;
      if (isSelected) {
        const isTermNotSelected = this.isShareTermNotSelected(shareBaseId, i);
        if (isTermNotSelected) continue;

        if (accountNames) {
          accountNames.push(this.statefulService.wrap(`${shareBaseId}/${i}/name`).valueString);
        }
      }
    }
    return accountNames.join(', ');
  }

  private isShareTermNotSelected(shareBaseId: string, i: number) {
    const type = this.statefulService.wrap(`${shareBaseId}/${i}/type`).valueStringOnly;
    const isTermSelected = this.statefulService.wrap(`${shareBaseId}/${i}/terms/0/isSelected`).value;
    return SHARE_CERT_TYPE.includes(type) && !isTermSelected;
  }

  /**
   * update bundle data to DDMS
   */
  public updateBundleDataToDDMS(
    flowId?: string,
    cb?: (res: HttpStatusCode) => void,
    err?: (res: HttpStatusCode) => void,
    isFinalSave?: boolean
  ): void {
    let obj = 'bundle' as keyof typeof ApplicationApiService.prototype.apiPath;
    if (!this.crudApiService.authorization.value) return; // To update bundle authorization always required
    this.storagePrefix = 'api';
    if (flowId === LoanType.OLA.toLowerCase()) {
      obj += 'OLA';
      this.storagePrefix = LoanType.OLA.toLowerCase();
    }

    // Remove the FlowId check when we remove the Partial Save Implementation for OAO as well
    if (flowId === LoanType.OLA.toLowerCase() && this.isNonPartialOLAFeatureFlagEnabled()) {
      this.processNonPartialSave(cb, err, isFinalSave, flowId);
    } else {
      this.prepareUrl(obj, (url) => {
        // When Save Application Data to Session Storage Feature Flag is turned ON
        this.processData(url, isFinalSave, cb, err);
      });
    }
  }

  /**
   * Method to handle Non-Partial Page level Save
   * @param cb - callback action
   * @param err - error action
   * @param isFinalSave - flag to determine if it's a final submission
   * @param flowId - to determine if it's OLA or OAO
   */
  public processNonPartialSave(
    cb: (res: HttpStatusCode) => void,
    err: (res: HttpStatusCode) => void,
    isFinalSave: boolean,
    flowId: string
  ): void {
    // Prepare the URL for the HTTP request
    this.prepareUrl('save', (url) => {
      this.partialApplication = false;
      let payload = null;
      const currentRequestBody = this.statefulService.wrap('ss:current-patch-request-body').valueString;
      if (currentRequestBody && currentRequestBody.length > 0) {
        payload = currentRequestBody;
      }
      // Use HttpClient to send the POST request
      this.httpClient.post(url, payload, this.httpOptions()).subscribe(
        // Handle the response
        (res: ApiResult<BundleResponse>) => {
          if (res?.isSuccess) {
            this.deleteSavedValuesFromSS(JSON.parse(payload));
            if (isFinalSave) {
              this.statefulService.wrap('ss:current-patch-request-body').value = null;
            }
            if (cb) cb(HttpStatusCode.Ok);
          }
        },
        // If the HTTP request fails
        (error) => {
          if (error?.status === HttpStatusCode.Conflict && !error?.error.isSuccess) {
            err(HttpStatusCode.Conflict);
            return;
          }

          this.logInsightsEvent(flowId, 'Error: OLA Non-Partial Save');
          const errObj = error?.error;
          this.globalErrorHandler(errObj);
          if (err) {
            err(error?.status);
          }
        }
      );
    });
  }

  /**
   * Method to log events related to Save
   * @param flowId - to determine if it's OLA or OAO
   * @param message - determine the message to be shown in the logs
   */
  public logInsightsEvent(flowId: string, message: string): void {
    const logName = flowId === LoanType.OLA.toLowerCase() ? 'ecp.ui.olaNonPartial_Save' : 'ecp.ui.oaoNonPartial_Save';
    const userInfo = this.buildCustomUserInfoData(flowId);
    this.logEvent(logName, {
      info: userInfo,
      message
    });
  }

  /**
   * Method to build/construct User Info data needed for appinsights
   * @returns userInfo of type:CustomUserInfo
   */
  public buildCustomUserInfoData(flowId: string): CustomUserInfo {
    const isOLA = flowId === LoanType.OLA.toLowerCase();
    const userFirstName = isOLA
      ? this.statefulService.wrap('ola:borrowerInfo/firstName').valueString
      : this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/firstName').valueString;
    const userLastName = isOLA
      ? this.statefulService.wrap('ola:borrowerInfo/lastName').valueString
      : this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/lastName').valueString;
    const userEmail = isOLA
      ? this.statefulService.wrap('ola:borrowerInfo/emailAddress').valueString
      : this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/email').valueString;
    const applicationId = isOLA
      ? null
      : this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString;
    const primaryApplicantApplicantId = isOLA
      ? null
      : this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/primaryApplicant/applicantId').valueString;

    const userInfo = {
      applicantId: primaryApplicantApplicantId,
      applicationId,
      email: userEmail,
      lastName: userLastName,
      firstName: userFirstName
    } as CustomUserInfo;

    return userInfo;
  }

  public patchChangedValues(loanType: string, cb: (isSuccess: boolean, error?) => void): void {
    let payload = null;
    const currentRequestBody = this.statefulService.wrap('ss:current-patch-request-body').valueString;
    if (currentRequestBody && currentRequestBody.length > 0) {
      payload = currentRequestBody;
      this.bundle(payload, loanType).subscribe(
        () => {
          this.deleteSavedValuesFromSS(JSON.parse(payload));
          cb(true);
        },
        (error) => cb(false, error)
      );
    } else cb(true);
  }
  /**
   * Method to process data when Save Application Data to Session Storage Feature Flag is turned ON
   * @param url - API url
   * @param isFinalSave - flag to determine if it's a final submission
   * @param cb - callback action
   * @param err - error action
   */
  private processData(url: string, isFinalSave?: boolean, cb?: (res: HttpStatusCode) => void, err?: (res: HttpStatusCode) => void): void {
    let payload = null;
    const currentRequestBody = this.statefulService.wrap('ss:current-patch-request-body').valueString;
    if (currentRequestBody && currentRequestBody.length > 0) {
      payload = currentRequestBody;
    }
    const source = () => this.httpClient.put(url, payload, this.httpOptions());

    CrudApiBackend.requestQueue.addItem(source).subscribe({
      next: (res: ApiResult<BundleResponse>) => {
        if (res?.isSuccess) {
          this.deleteSavedValuesFromSS(JSON.parse(payload));
          if (isFinalSave) {
            this.statefulService.wrap('ss:current-patch-request-body').value = null;
          }
        }
        // When the response.isSuccess flag return false: it means the PATCH failed for the current payload.
        // The application flow still continues as we're going to re-patch it in the next call
        // But should we allow the User to proceed even though a PATCH fails on Final PUT..?
        // Need to discuss/get thoughts from Leadership
        if (cb) cb(HttpStatusCode.Ok);
      },
      error: (error) => {
        const errObj = error?.error;
        this.globalErrorHandler(errObj);
        if (err) {
          err(error?.status);
        }
      }
    });
  }

  /**
   * Method to delete Saved values from last PUT request and updating the Session variables
   */
  private deleteSavedValuesFromSS(payload: { timestamp }[]): void {
    if (payload?.length > 0) {
      const currentRequestBodyInSS = this.statefulService.wrap('ss:current-patch-request-body').valueString;
      if (currentRequestBodyInSS && currentRequestBodyInSS.length > 0) {
        let currentRequestBodyInSSParsedValue = [];
        currentRequestBodyInSSParsedValue = JSON.parse(currentRequestBodyInSS);
        payload.forEach(
          (data) => (currentRequestBodyInSSParsedValue = currentRequestBodyInSSParsedValue.filter((t) => t.timestamp !== data.timestamp))
        );
        this.statefulService.wrap('ss:current-patch-request-body').value = JSON.stringify(currentRequestBodyInSSParsedValue);
      }
    }
  }

  /**
   * Unified options argument for HTTP calls
   */
  protected httpOptions(flowId?: string, token?: string): { headers: HttpHeaders } {
    const httpOptions = {
      headers: this.crudApiService.headers(token)
    };
    return httpOptions;
  }

  protected httpOptionsBlob(): object {
    return {
      headers: this.crudApiService.headers().delete('Content-Type'),
      responseType: 'blob' as 'json'
    };
  }

  /**
   * Async URL building. BorrowerId and isPrimary required for coApplicant integration calls.
   * @param id URL type name
   * @param callback function to call when URL is built
   * @param isPrimary default to true for primary applicant.
   */
  protected prepareUrl(
    id: keyof typeof ApplicationApiService.prototype.apiPath,
    callback: (url: string) => void,
    borrowerId?: string,
    isPrimary = true
  ): void {
    this.crudApiService.checkBundle(() => {
      const loanNumber = this.statefulService.wrap('api:loan/loanNumber');
      this.apiRoot = this.apiService.GetFullURL('consumerPortalApi', '');
      const redirectHostname = this.isBrowser ? `${window.location.protocol}//${window.location.host}` : '';
      const urlObj = new URL(this.apiRoot + this.apiPath[id].replace(/{{redirectHostname}}/g, redirectHostname));

      if (id === 'pricingSchedule' && borrowerId) {
        urlObj.searchParams.set('productId', borrowerId);
      } else if (borrowerId) {
        if (id !== 'rateSheet') {
          urlObj.searchParams.set('borrowerId', borrowerId);
        }
      }

      if (id !== 'save' && id !== 'finalSubmit') {
        urlObj.searchParams.set('isPrimary', isPrimary.toString());
      }
      // Add Partial Save URL Params
      if (this.partialApplication) {
        urlObj.searchParams.set('allowPartialSave', 'true');
      }

      // Add generate App Id to URL Params
      if (this.generateAppId) {
        urlObj.searchParams.set('generateAppId', 'true');
      }

      const url = decodeURI(urlObj.href);

      if (url.indexOf('{{rateSheetId}}') !== -1) {
        callback(url.replace(/{{rateSheetId}}/g, String(borrowerId)));
        return;
      }

      if (url.indexOf('{{loanNumber}}') === -1 || loanNumber.value) {
        callback(url.replace(/{{loanNumber}}/g, String(loanNumber.value)));
        return;
      }

      loanNumber.load(() => {
        callback(url.replace(/{{loanNumber}}/g, String(loanNumber.value)));
      });
    });
  }

  /**
   * Async URL building. BorrowerId and isPrimary required for coApplicant integration calls.
   * @param id URL type name
   * @param callback function to call when URL is built
   * @param isPrimary default to true for primary applicant.
   */
  protected prepareUrlForPost(
    id: keyof typeof ApplicationApiService.prototype.apiPath,
    callback: (url: string) => void,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    borrowerId?: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isPrimary = true
  ): void {
    this.crudApiService.checkBundle(() => {
      const loanNumber = this.statefulService.wrap('api:loan/loanNumber');
      this.apiRoot = this.apiService.GetFullURL('consumerPortalApi', '');
      const urlObj = new URL(this.apiRoot + this.apiPath[id]);

      const url = decodeURI(urlObj.href);

      if (url.indexOf('{{loanNumber}}') === -1 || loanNumber.value) {
        callback(url.replace(/{{loanNumber}}/g, String(loanNumber.value)));
        return;
      }

      loanNumber.load(() => {
        callback(url.replace(/{{loanNumber}}/g, String(loanNumber.value)));
      });
    });
  }

  /**
   * bundle
   */
  public bundle(rawReqBody: object, loanType?: string): Observable<BundleResponse> {
    const url = `consumer${loanType === LoanTypeEnum.OLA.toLowerCase() ? '/ola' : ''}/dynamic/bundle`;
    const ulObj = {
      hostName: this.hostName,
      method: 'PATCH',
      url,
      defaultHeaders: {
        [RequestHeaderConst.authorization]: this.crudApiService.authorization.value,
        [RequestHeaderConst.dxSessionId]: this.crudApiService.authorization.value,
        [RequestHeaderConst.contentTypeName]: 'application/json'
      }
    } as DataLoadURL;

    return this.apiService.request<BundleResponse>({ urlObj: ulObj, body: rawReqBody });
  }

  /**
   * RequestedDocUpload
   */
  public RequestedDocUpload(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    files: any,
    documentMetadata: any,
    docType: string | null,
    fileName: string | null,
    docCategory: string | null,
    callback: (cb: Document[], error: any[]) => void
  ): void {
    const success = [];
    const ulObj = {
      hostName: this.hostName,
      method: 'POST',
      url: 'uploaddocument',
      defaultHeaders: {
        [RequestHeaderConst.authorization]: this.crudApiService.authorization.value,
        [RequestHeaderConst.dxSessionId]: this.crudApiService.authorization.value
      }
    } as DataLoadURL;
    const params = {
      documentType: docType,
      renamedFile: '',
      documentCategory: docCategory
    };
    let count = 0;
    const fileLength = files.filter((t) => t.status === Info.queued).length;
    files
      .filter((t) => t.status === Info.queued)
      .forEach((item) => {
        if (item.upload) {
          documentMetadata.name = item.upload.filename;
          documentMetadata.contentMimeType = item.type;
          params.renamedFile = `${fileName}.${documentMetadata?.name?.split('.')[1]}`;
        }
        const formData: FormData = new FormData();
        formData.append(Info.file, item);
        formData.append(Info.documentRequest, JSON.stringify(documentMetadata));

        this.apiService
          .request({
            body: formData,
            urlObj: ulObj,
            queryParam: params
          })
          .toPromise()
          .then((response: Document) => {
            files
              .filter((t) => t.upload.filename === response.name)
              .forEach((t) => {
                t.status = Info.uploaded;
              });
            success.push(response);
            if (count === fileLength - 1) {
              const erroredFiles = files.filter((t) => t.status !== Info.uploaded);
              callback(success, erroredFiles);
            }
            count++;
          })
          .catch((err) => {
            if (count === fileLength - 1) {
              const erroredFiles = files.filter((t) => t.status !== Info.uploaded);
              callback(success, erroredFiles);
              this.logger.withError(err).error('File upload encountered an error', { component: 'ApplicationApiService' });
            }
            count++;
          });
      });
  }

  /**
   * Private method to handle errors from API requests.
   *
   * @remarks removed the logic to try and extract a particular piece of this error message - lets get the full error -
   * however, we may need to revist this
   *
   * @param error - The data object from an API response
   */
  private globalErrorHandler(error): void {
    // Log the error message using SawDust
    this.logger.withError(error).error('Application API global error handler invoked', { component: 'ApplicationApiService' });
  }

  public silentRegistration(postData: any, token: string, cb: (response: any) => void, err?: (response: any) => void): void {
    // Prepare the URL for the HTTP request
    this.prepareUrl(!!token ? 'silentRegister' : 'register', (url) => {
      // Use HttpClient to send the POST request
      this.httpClient.post(url, postData, this.httpOptions(null, token)).subscribe(
        // Handle the response
        (data: ApiResult<any>) => {
          // NOTE: HTTP errors should trigger the error below, we won't get much info from the response otherwise
          cb(data);
        },
        // If the HTTP request fails
        (error: HttpErrorResponse) => {
          if (err) {
            err(error);
          }
        }
      );
    });
  }

  public verifiedRegistration(postData: any, cb: (response: any) => void, err?: (response: any) => void): void {
    // Prepare the URL for the HTTP request
    this.prepareUrl('register', (url) => {
      // Use HttpClient to send the POST request
      this.httpClient.post(url, postData, this.httpOptions()).subscribe(
        // Handle the response
        (data: ApiResult<any>) => {
          // NOTE: HTTP errors should trigger the error below, we won't get much info from the response otherwise
          cb(data);
        },
        // If the HTTP request fails
        (error: HttpErrorResponse) => {
          if (err) {
            err(error);
          }
        }
      );
    });
  }

  public login(login: ConsumerLogin, oldSession: string): Observable<Result<LoginResponse>> {
    User_api.LogIn.defaultHeaders = oldSession
      ? { [RequestHeaderConst.authorization]: this.statefulService.wrap('ss:authorization').valueString, Persistedtoken: oldSession }
      : { [RequestHeaderConst.authorization]: this.statefulService.wrap('ss:authorization').valueString };
    return this.apiService.request<Result<LoginResponse>>({
      body: login,
      urlObj: User_api.LogIn
    });
  }

  /**
   * Checks if alloy is enabled.
   * @returns True if alloy is enabled for flow
   */
  public isAlloyEnabled(): boolean {
    const state = this.alloy.isAlloyEnabled();
    return state.enabled;
  }

  /**
   * Gets a value of whether or not we hard stop on denied (red path).
   * @returns True if stop on denied is enabled for the journey, otherwise false.
   */
  public isAlloyStopOnDeniedEnabled(): boolean {
    return this.alloy.isDeniedBlocking();
  }

  /**
   * Sets up the init args for alloy.
   * @param mode The mode the workflow is running in, either api (oao) or ola
   * @returns The init args
   */
  public getAlloyInitArgs(mode: string): { sdkKey: string; journeyToken: string; sourcePublicToken?: string } {
    return this.alloy.getAlloyInitArgs(mode);
  }

  /**
   * Called at the beginning of alloy verification to establish data points for getting tracking data
   * @param key The sdk key to call for init
   * @param journeyToken The journey token to use
   * @param journeyApplicationToken the journey application token to use
   * @param socurePublicToken the socure public token if provided
   * @returns an observable with the user tracking keys if enabled
   */
  public alloyTrackingAndInit(
    key: string,
    journeyToken: string,
    journeyApplicationToken?: string,
    socurePublicToken?: string,
    forceReInit?: boolean
  ): Observable<{
    neuroUserId?: string;
    neuroSiteId?: string;
    socureDeviceId?: string;
    threatMetrixSessionId?: string;
    iovationBlackboxId?: string;
  }> {
    const alloyJourneyKeys: {
      key: string;
      journeyToken: string;
      journeyApplicationToken: string;
      socurePublicToken?: string;
      isNext?: boolean;
    } = {
      key,
      journeyApplicationToken,
      journeyToken,
      socurePublicToken
    };
    if (socurePublicToken) {
      alloyJourneyKeys.isNext = true;
    }
    return this.alloy.init(alloyJourneyKeys, forceReInit);
  }

  /**
   * Provides a creation or update of an alloy journey.
   * @param entityId The id of the application
   * @returns An observable of the alloy result data
   */
  public startAlloyJourney(
    entityId: string,
    neuroUserId?: string,
    neuroSiteId?: string,
    socureDeviceId?: string,
    threatMetrixSessionId?: string,
    iovationBlackBoxId?: string
  ): Observable<AlloyVerificationResult> {
    const requestData: {
      entityId: string;
      neuroUserId?: string;
      neuroSiteId?: string;
      socureDeviceId?: string;
      threatMetrixSessionId?: string;
      iovationBlackBoxId?: string;
    } = {
      entityId,
      neuroUserId,
      neuroSiteId,
      socureDeviceId,
      threatMetrixSessionId,
      iovationBlackBoxId
    };
    const eventPairId = crypto.randomUUID();
    this.logEvent('ecp.ui.alloy', {
      eventState: 'start',
      correlationId: eventPairId,
      message: 'Alloy journey create'
    });
    return new Observable<AlloyVerificationResult>((sub) => {
      this.prepareUrl('alloyJourney', (url) => {
        this.httpClient.post<ApiResult<AlloyVerificationResult>>(url, requestData, this.httpOptions()).subscribe({
          next: (res) => {
            sub.next(res.data);
            this.logEvent('ecp.ui.alloy', {
              eventState: 'end',
              correlationId: eventPairId,
              message: `${res.data.outcome}|${res.data.status}`
            });
          },
          error: (err) => {
            sub.error(err);
            this.logEvent('ecp.ui.alloy', {
              eventState: 'end',
              correlationId: eventPairId,
              message: 'error'
            });
          },
          complete: () => sub.complete()
        });
      });
    });
  }

  /**
   * Trigger a step up event in alloy - this will cause an iframe to pop up.
   * @param key The sdk key provided by the lender configuration.
   * @param journeyToken The journey token provided by the lender configuration.
   * @param journeyApplicationToken The application token provided by starting the alloy journey.
   * @param socurePublicToken The optional public token to use socure.
   * @returns A result from the step up in an observable.
   */
  public triggerAlloyStepup(
    key: string,
    journeyToken: string,
    journeyApplicationToken: string,
    socurePublicToken?: string
  ): Observable<AlloyVerificationResult> {
    const alloyJourneyKeys: {
      key: string;
      journeyToken: string;
      journeyApplicationToken: string;
      socurePublicToken?: string;
      isNext?: boolean;
    } = {
      key,
      journeyApplicationToken,
      journeyToken
    };
    if (socurePublicToken) {
      alloyJourneyKeys.isNext = true;
      alloyJourneyKeys.socurePublicToken = socurePublicToken;
    }
    return new Observable<AlloyVerificationResult>((sub) => {
      if (alloyJourneyKeys) {
        const eventPairId = crypto.randomUUID();
        this.logEvent('ecp.ui.alloy.stepup', {
          eventState: 'start',
          correlationId: eventPairId,
          message: 'step-up'
        });
        this.alloy
          .init(alloyJourneyKeys, true) // Force re-init to ensure the latest keys are used, including journey app token
          .pipe(
            mergeMap(() => {
              return this.alloy.verifyApplication();
            })
          )
          .subscribe({
            next: (res) => {
              this.logEvent('ecp.ui.alloy.stepup', {
                eventState: 'end',
                correlationId: eventPairId,
                message: `${res.outcome}|${res.status}`
              });
              sub.next(res);
            },
            error: (err) => {
              sub.error(err);
              this.logEvent('ecp.ui.alloy.stepup', {
                eventState: 'end',
                correlationId: eventPairId,
                message: 'error'
              });
            },
            complete: () => sub.complete()
          });
      } else {
        sub.next(null);
        sub.complete();
      }
    });
  }

  /**
   *
   * @param param0
   */
  /**
   * Minimal prefill link generation: posts data and returns ApiResult<AlloyVerificationResult>.
   */
  public generatePrefillLink(data: AlloyPrefillData): Observable<ApiResult<AlloyVerificationResult>> {
    return new Observable<ApiResult<AlloyVerificationResult>>((sub) => {
      const eventPairId = crypto.randomUUID();
      this.logEvent('ecp.ui.alloy.prefill', {
        eventState: 'start',
        correlationId: eventPairId,
        message: 'Generating alloy prefill link'
      });
      // Build a dynamic, clickable prefill URL for local debugging.
      // Format: {origin}/prefill/{sessionId}/1?vfp={verifyCode}
      // sessionId: pulled from current auth header value
      // verifyCode: Misc
      try {
        const isSandboxMode = this.lenderService.lenderData.alloySettings.sandboxMode;
        if (isSandboxMode) {
          const sessionId = this.crudApiService.authorization.value;
          const vfpHash = new Date()
            .toISOString()
            .replace(/[-:.TZ]/g, '')
            .slice(0, 12);
          const prefillUrl = `${origin}/prefill/${sessionId}/${data.applicantIndex}?vfp=${vfpHash}`;
          // NOTE: this must go to the console in all environments when in sandbox - not sawdust
          // as it is needed for completing the prefill flow if a text service is not set up in alloy.
          // eslint-disable-next-line no-restricted-properties
          console.log('Alloy Prefill URL:', prefillUrl);
        }
      } catch (e) {
        // Swallow any unexpected errors so we don't interfere with normal flow
        this.logger.withError(e).warn('Failed to build Alloy Prefill URL log', { component: 'ApplicationApiService' });
      }
      this.prepareUrl('alloyPrefillLink', (url) => {
        this.httpClient.post<ApiResult<AlloyVerificationResult>>(url, data, this.httpOptions()).subscribe({
          next: (res) => {
            this.logEvent('ecp.ui.alloy.prefill', {
              eventState: 'end',
              correlationId: eventPairId,
              message: res?.isSuccess ? 'prefill-link-success' : `prefill-link-failed|${res?.reason || 'no-reason'}`
            });
            sub.next(res);
          },
          error: (err) => {
            this.logEvent('ecp.ui.alloy.prefill', {
              eventState: 'end',
              correlationId: eventPairId,
              message: 'error'
            });
            sub.error(err);
          },
          complete: () => sub.complete()
        });
      });
    });
  }

  /**
   * Marks the primary applicant's Alloy prefill process as complete.
   * Mirrors the style of generatePrefillLink / verifyPrefillLink for consistency.
   * Backend endpoint: alloyPrefillPrimaryComplete (POST)
   *
   * @param data Arbitrary payload needed by backend to finalize primary prefill (e.g. applicantIndex, optional sessionId)
   * If sessionId isn't provided, it will be injected from the current authorization value (same pattern as verifyPrefillLink).
   */
  public completePrimaryAlloyPrefill(data): Observable<ApiResult<{ access_token: string; refresh_token: string }>> {
    const eventPairId = crypto.randomUUID();
    this.logEvent('ecp.ui.alloy.prefill.primary.complete', {
      eventState: 'start',
      correlationId: eventPairId,
      message: 'Completing primary alloy prefill'
    });

    return new Observable<ApiResult<{ access_token: string; refresh_token: string }>>((sub) => {
      this.prepareUrl('alloyPrefillPrimaryComplete', (url) => {
        this.httpClient.post<ApiResult<{ access_token: string; refresh_token: string }>>(url, data, this.httpOptions()).subscribe({
          next: (res) => {
            this.logEvent('ecp.ui.alloy.prefill.primary.complete', {
              eventState: 'end',
              correlationId: eventPairId,
              message: res?.isSuccess ? 'primary-complete-success' : `primary-complete-failed|${res?.reason || 'no-reason'}`
            });
            sub.next(res);
          },
          error: (err) => {
            this.logEvent('ecp.ui.alloy.prefill.primary.complete', {
              eventState: 'end',
              correlationId: eventPairId,
              message: 'error'
            });
            sub.error(err);
          },
          complete: () => sub.complete()
        });
      });
    });
  }

  /**
   *
   */
  public resendPrefillLink(data): Observable<ApiResult<boolean>> {
    return new Observable<ApiResult<boolean>>((sub) => {
      const eventPairId = crypto.randomUUID();
      this.logEvent('ecp.ui.alloy.prefill', {
        eventState: 'start',
        correlationId: eventPairId,
        message: 'Resending alloy prefill link'
      });

      try {
        const isSandboxMode = this.lenderService.lenderData.alloySettings.sandboxMode;
        if (isSandboxMode) {
          const sessionId = this.crudApiService.authorization.value;
          const vfpHash = new Date()
            .toISOString()
            .replace(/[-:.TZ]/g, '')
            .slice(0, 12);
          const prefillUrl = `${origin}/prefill/${sessionId}/${data.applicantIndex}?vfp=${vfpHash}`;
          this.logger.info('Alloy Prefill URL:', prefillUrl, { component: 'ApplicationApiService' });
        }
      } catch (e) {
        // Swallow any unexpected errors so we don't interfere with normal flow
        this.logger.withError(e).warn('Failed to build Alloy Prefill URL log', { component: 'ApplicationApiService' });
      }
      // Only inject sessionId from auth header if caller did not supply one
      if (!data.sessionId) {
        data.sessionId = this.crudApiService.authorization.value;
      }

      this.prepareUrl('alloyPrefillResendLink', (url) => {
        const finalUrl = url.replace('{{index}}', data.applicantIndex.toString());
        // Only email and entityId expected in body
        this.httpClient.post<ApiResult<boolean>>(finalUrl, data, this.httpOptions()).subscribe({
          next: (res) => {
            this.logEvent('ecp.ui.alloy.prefill', {
              eventState: 'end',
              correlationId: eventPairId,
              message: res?.isSuccess ? 'resend-link-success' : 'resend-link-failed'
            });
            sub.next(res);
          },
          error: (err) => {
            this.logEvent('ecp.ui.alloy.prefill', {
              eventState: 'end',
              correlationId: eventPairId,
              message: 'error'
            });
            sub.error(err);
          },
          complete: () => sub.complete()
        });
      });
    });
  }

  /**
   *
   * @param data - Data sent to prefill verify endpoint
   * @returns
   */
  public verifyPrefillLink(data): Observable<ApiResult<AlloyVerificationResult>> {
    const eventPairId = crypto.randomUUID();
    this.logEvent('ecp.ui.alloy.verify', {
      eventState: 'start',
      correlationId: eventPairId,
      message: 'Verifying alloy prefill link'
    });

    // Only inject sessionId from auth header if caller did not supply one
    if (!data.sessionId) {
      data.sessionId = this.crudApiService.authorization.value;
    }

    return new Observable<ApiResult<AlloyVerificationResult>>((sub) => {
      this.prepareUrl('alloyPrefillLinkVerify', (url) => {
        this.httpClient.post<ApiResult<AlloyVerificationResult>>(url, data, this.httpOptions()).subscribe({
          next: (res) => {
            this.logEvent('ecp.ui.alloy.verify', {
              eventState: 'end',
              correlationId: eventPairId,
              message: res?.isSuccess ? 'verify-success' : `verify-failed|${res?.reason || 'no-reason'}`
            });
            sub.next(res);
          },
          error: (err) => {
            this.logEvent('ecp.ui.alloy.verify', {
              eventState: 'end',
              correlationId: eventPairId,
              message: 'error'
            });
            sub.error(err);
          },
          complete: () => sub.complete()
        });
      });
    });
  }

  public ensureAlloySaved(flow: string): void {
    this.updateBundleDataToDDMS(flow);
  }

  /**
   * Log External Service response into ArcOS
   * @param externalData External Servce data
   * @returns boolean
   */
  public logExternalServiceDataToArcOS(externalData: ExternalServiceData): Observable<ApiResult<boolean>> {
    const eventPairId = crypto.randomUUID();
    this.logEvent('ecp.ui.alloy', {
      eventState: 'start',
      correlationId: eventPairId,
      message: 'Logging Alloy Stepup response into arcOS Logging'
    });
    return new Observable<ApiResult<boolean>>((sub) => {
      this.prepareUrl('logexternalservicedata', (url) => {
        this.httpClient.post<ApiResult<ApiResult<boolean>>>(url, externalData, this.httpOptions()).subscribe({
          next: (res) => {
            sub.next(res.data);
            this.logEvent('ecp.ui.alloy', {
              eventState: 'end',
              correlationId: eventPairId,
              message: `External service data successfully logged into arcOS.`
            });
          },
          error: (err) => {
            sub.error(err);
            this.logEvent('ecp.ui.alloy', {
              eventState: 'end',
              correlationId: eventPairId,
              message: 'error'
            });
          },
          complete: () => sub.complete()
        });
      });
    });
  }

  /**
   * Save Alloy Journey Outcome into arcOS
   * @param bundleEntityId entityId string
   * @returns boolean
   */
  public saveAlloyJourneyOutcomeToArcOS(alloyResult: AlloyVerificationResult): Observable<ApiResult<boolean>> {
    const eventPairId = crypto.randomUUID();
    this.logEvent('ecp.ui.alloy', {
      eventState: 'start',
      correlationId: eventPairId,
      message: 'Logging Save Alloy Outcome into arcOS Logging'
    });
    return new Observable<ApiResult<boolean>>((sub) => {
      this.prepareUrl('savejourney', (url) => {
        this.httpClient.post<ApiResult<ApiResult<boolean>>>(url, JSON.stringify(alloyResult), this.httpOptions()).subscribe({
          next: (res) => {
            sub.next(res.data);
            this.logEvent('ecp.ui.alloy', {
              eventState: 'end',
              correlationId: eventPairId,
              message: `Alloy Outcome successfully saved into arcOS.`
            });
          },
          error: (err) => {
            sub.error(err);
            this.logEvent('ecp.ui.alloy', {
              eventState: 'end',
              correlationId: eventPairId,
              message: 'error'
            });
          },
          complete: () => sub.complete()
        });
      });
    });
  }

  /** Method which tells you whether the Non Partial OLA Feature Flag is Enabled or Not */
  public isNonPartialOLAFeatureFlagEnabled(): boolean {
    return this.featureFlagService.isFeatureEnabled('NonPartialOLA_v2');
  }
}
